import { PropsWithChildren, ReactNode, useEffect } from 'react';

import { css } from '@compiled/react';

import { ConfluenceLogo, LoomLogo } from '@atlaskit/logo';
import { JiraLogo, JiraProductDiscoveryLogo } from '@atlaskit/logo-next';
import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
	ConfluenceBackground,
	JiraBackground,
	LoomBackground,
	JiraProductDiscoveryBackground,
	TwcBackground,
} from '@atlassian/product-wireframes';
import { JiraServiceManagementBackground } from '@atlassian/product-wireframes/jira-service-management';
import { ServCoBackground } from '@atlassian/product-wireframes/service-collection';

import { ServiceCollectionLogoV2 } from '@/components/signup/utils/logos/service-collection-logo-v2';
import { TeamworkCollectionLogo } from '@/components/signup/utils/logos/teamwork-collection-logo';
import { ClientOnly } from '@/utils/client-only';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import { Modal, ModalSize } from './modal';
import { useIsJsmCrossflow } from './use-is-jsm-crossflow';
import { persistJsmCrossflow } from './use-jsm-crossflow-persistence';

type ProductOverlayTemplateProps = PropsWithChildren<{
	clientOnly?: boolean;
	header?: string;
	headerLogo?: ReactNode;
	subheader?: string;
	size?: ModalSize;
	productLogo?: ReactNode;
	productBackground: React.ComponentType<{ children: ReactNode }>;
	showBrandFooter?: boolean;
	showProductLogoOnLargeScreens?: boolean;
	fadeOutContent?: boolean;
	moveLogoToModal?: boolean;
}>;

// eslint-disable-next-line complexity
const ProductOverlayTemplate = ({
	children,
	clientOnly = true,
	header,
	headerLogo,
	subheader,
	size,
	moveLogoToModal,
	productLogo,
	productBackground,
	showBrandFooter = true,
	showProductLogoOnLargeScreens = false,
	fadeOutContent = false,
}: // eslint-disable-next-line complexity
ProductOverlayTemplateProps) => {
	const ProductBackground = productBackground;
	const visualFeedback = checkFeatureFlagGate(featureFlags.signupVisualAlignmentFeedback);
	const template = (
		<ProductBackground>
			<Modal
				showProductLogoOnLargeScreens={showProductLogoOnLargeScreens}
				size={size}
				showBrandFooter={showBrandFooter}
				productLogo={productLogo}
				moveLogoToModal={moveLogoToModal}
			>
				<div
					css={[
						innerModalCss,
						size === 'sm' && innerModalSmCss,
						headerLogo && innerModalWithHeaderLogoCss,
						visualFeedback && innerModalVisualFeedbackCss,
					]}
				>
					{!moveLogoToModal && (
						<div css={[productLogoContainerCss, hideAboveSm]}>{productLogo}</div>
					)}
					{headerLogo && <div css={headerLogoContainerCss}>{headerLogo}</div>}
					{header && <h1 css={[headerCss, fadeOutContent && fade]}>{header}</h1>}
					{subheader && <p css={[subheaderCss, fadeOutContent && fade]}>{subheader}</p>}
					{children}
				</div>
			</Modal>
		</ProductBackground>
	);

	if (clientOnly) {
		return <ClientOnly>{template}</ClientOnly>;
	}

	return template;
};

export const ConfluenceOverlayTemplate = ({
	children,
	header,
	subheader,
	size,
}: PropsWithChildren<{
	header?: string;
	subheader?: string;
	size?: ModalSize;
}>) => {
	return (
		<ProductOverlayTemplate
			productLogo={<ConfluenceLogo appearance="brand" size="medium" />}
			productBackground={ConfluenceBackground}
			header={header}
			subheader={subheader}
			size={size}
			showBrandFooter={false}
			clientOnly={false}
		>
			{children}
		</ProductOverlayTemplate>
	);
};

// Backward compatibility: Keep the JiraOverlayTemplate as a wrapper
export const JiraOverlayTemplate = ({
	children,
	header,
	subheader,
	size,
	fadeOutContent,
}: PropsWithChildren<{
	header?: string;
	subheader?: string;
	size?: ModalSize;
	fadeOutContent?: boolean;
}>) => {
	return (
		<ProductOverlayTemplate
			productLogo={<JiraLogo size="small" appearance="brand" shouldUseNewLogoDesign />}
			productBackground={JiraBackground}
			header={header}
			subheader={subheader}
			size={size}
			clientOnly={false}
			fadeOutContent={fadeOutContent}
			showBrandFooter={false}
		>
			{children}
		</ProductOverlayTemplate>
	);
};

// Loom overlay template using the parameterized component
export const LoomOverlayTemplate = ({
	children,
	header,
	subheader,
	size,
}: PropsWithChildren<{
	header?: string;
	subheader?: string;
	size?: ModalSize;
}>) => {
	return (
		<ProductOverlayTemplate
			productLogo={<LoomLogo size="small" appearance="brand" />}
			productBackground={LoomBackground}
			header={header}
			subheader={subheader}
			size={size}
			showBrandFooter={false}
		>
			{children}
		</ProductOverlayTemplate>
	);
};

// Pin the JSM wireframe to the viewport so it doesn't scroll with the modal
// content. The Blanket (modal) already uses position: fixed and renders on top.
// The wireframe is rendered by `@atlassian/product-wireframes` as a sibling to
// the modal, so we must target it via its testId since the package exposes no
// other hook.
const fixedWireframeBackgroundCss = css({
	[media.above.sm]: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > [data-testid="above-small-screen"]': {
			position: 'fixed',
			inset: 0,
		},
	},
});

const FixedServCoBackground = ({ children }: { children: ReactNode }) => (
	<div css={fixedWireframeBackgroundCss}>
		<ServCoBackground>{children}</ServCoBackground>
	</div>
);

const FixedJsmBackground = ({ children }: { children: ReactNode }) => (
	<div css={fixedWireframeBackgroundCss}>
		<JiraServiceManagementBackground>{children}</JiraServiceManagementBackground>
	</div>
);

// Service Collection signup overlay. Background reflects the entry point:
//   - From a JSM page (see useIsJsmCrossflow) → JiraServiceManagementBackground
//   - Otherwise (direct Service Collection entry) → ServCoBackground
//
// `isJsmCrossflow` may be passed to override the auto-detection. Use this for
// post-signup screens (confirmation, onboarding) where the URL/referrer
// signals are no longer available but the caller has product context (e.g.
// the `products` array from search params).
export const JSMOverlayTemplate = ({
	children,
	header,
	headerLogo,
	subheader,
	size,
	showBrandFooter,
	hideLogo = false,
	isJsmCrossflow: isJsmCrossflowProp,
}: PropsWithChildren<{
	header?: string;
	headerLogo?: ReactNode;
	subheader?: string;
	size?: ModalSize;
	showBrandFooter?: boolean;
	hideLogo?: boolean;
	isJsmCrossflow?: boolean;
}>) => {
	const detectedJsmCrossflow = useIsJsmCrossflow();
	const isJsmCrossflow = isJsmCrossflowProp ?? detectedJsmCrossflow;
	const productBackground = isJsmCrossflow ? FixedJsmBackground : FixedServCoBackground;

	// Persist the resolved value so confirmation-page screens can read it back
	// after the URL no longer carries the original crossflow signals.
	useEffect(() => {
		if (isJsmCrossflowProp === undefined) persistJsmCrossflow(isJsmCrossflow);
	}, [isJsmCrossflow, isJsmCrossflowProp]);
	return (
		<ProductOverlayTemplate
			productLogo={hideLogo ? undefined : <ServiceCollectionLogoV2 />}
			productBackground={productBackground}
			header={header}
			headerLogo={headerLogo}
			subheader={subheader}
			size={size}
			showBrandFooter={showBrandFooter}
			showProductLogoOnLargeScreens={true}
			moveLogoToModal={true}
			// todo sync version and showBrandFooter when experiment finishes
			// https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/experiments/gwp_9785_servco_lofi_experiment
		>
			{children}
		</ProductOverlayTemplate>
	);
};

// JPD overlay template using the parameterized component
export const JPDOverlayTemplate = ({
	children,
	header,
	subheader,
	size,
}: PropsWithChildren<{
	header?: string;
	subheader?: string;
	size?: ModalSize;
}>) => {
	const productLogo = (
		<JiraProductDiscoveryLogo size="small" appearance="brand" shouldUseNewLogoDesign />
	);
	return (
		// TODO: once refreshed logos are made avaliable in WAC, update this to use the new @atlaskit/logo
		<ProductOverlayTemplate
			productLogo={productLogo}
			productBackground={JiraProductDiscoveryBackground}
			header={header}
			subheader={subheader}
			size={size}
			showBrandFooter={false}
		>
			{children}
		</ProductOverlayTemplate>
	);
};

// TWC overlay template using the parameterized component
export const TwcOverlayTemplate = ({
	children,
	header,
	subheader,
	size,
}: PropsWithChildren<{
	header?: string;
	subheader?: string;
	size?: ModalSize;
}>) => (
	// TODO: once refreshed logos are made avaliable in WAC, update this to use the new @atlaskit/logo
	<ProductOverlayTemplate
		productBackground={TwcBackground}
		header={header}
		subheader={subheader}
		size={size}
		productLogo={<TeamworkCollectionLogo shouldShowBrand={false} />}
		showBrandFooter={true}
		showProductLogoOnLargeScreens={true}
		moveLogoToModal={true}
	>
		{children}
	</ProductOverlayTemplate>
);

const hideAboveSm = css({
	[media.above.sm]: {
		display: 'none !important',
	},
});

const productLogoContainerCss = css({
	display: 'flex',
	justifyContent: 'center',
	marginBlockStart: '8px',
	marginBlockEnd: '16px',
});

const headerLogoContainerCss = css({
	display: 'flex',
	justifyContent: 'center',
	marginBlockEnd: token('space.300'),
});

const headerCss = css({
	color: token('color.text'),
	fontSize: 32,
	fontWeight: 700,
	lineHeight: 1.2,
	margin: '0',
	textAlign: 'center',
});

const subheaderCss = css({
	margin: '8px 0 24px',
	textAlign: 'center',
});

const fade = css({
	opacity: 0,
	transition: 'opacity 0.5s ease-in-out',
});

const innerModalCss = css({
	position: 'relative',
	alignItems: 'stretch',
	color: token('color.text.subtle'),
	display: 'flex',
	flexDirection: 'column',
	fontFamily: 'var(--atlassian-sans-font-family)',
	margin: '0 auto',
	width: '100%',
	minHeight: 560,
	[media.above.sm]: {
		paddingBlockStart: token('space.800', '64px'),
		paddingBlockEnd: token('space.800', '64px'),
		paddingInlineStart: token('space.800', '64px'),
		paddingInlineEnd: token('space.800', '64px'),
	},
	overflow: 'hidden',
});

const innerModalWithHeaderLogoCss = css({
	minHeight: 576,
	[media.above.sm]: {
		paddingBlockStart: token('space.500', '40px'),
		paddingBlockEnd: token('space.500', '40px'),
	},
});

// Gated by the signupVisualAlignmentFeedback feature flag.
// Less-rounded variant of the social sign-up button when rendered inside
// the product overlay. The LogoButton (in
// components/signup/account-creation/components/social) is presentation-
// agnostic and defaults to a pill shape (border-radius: 21) across WAC.
// Inside the product overlay (when the flag is on) we want a 6px radius
// (visually equivalent to the Atlaskit radius.medium token) for tighter
// visual alignment with the surrounding form fields and submit button
// (which already apply matching styling via the SubmitInput component in
// the templates folder).
const innerModalVisualFeedbackCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['[data-component="signup-social-button"]']: {
		// !important is used defensively to win the specificity contest
		// against LogoButton's own logoButtonCss `borderRadius: 21`. The
		// attribute-selector approach already gives us higher specificity
		// than the button's own class-based rule, but Compiled's atomic
		// CSS output can have non-deterministic insertion order at the
		// call-site level, so we belt-and-suspenders this for parity with
		// the original implementation. Remove !important if/when the gate
		// ships and the two values are reconciled at the source.
		borderRadius: '6px !important',
	},
});

const innerModalSmCss = css({
	maxWidth: 432,
	[media.above.sm]: {
		maxWidth: 560,
	},
});
